class CartDrawer extends HTMLElement {
  constructor() {
    super();
    
    // Add a flag to track when content is being rendered
    this.isRenderingContent = false;
    
    // Add animation timeout tracking
    this._openAnimationTimeout = null;
    this._renderTimeout = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.removeEventListener('click', this.open);
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Clear any existing animation timeouts to prevent conflicts
    if (this._openAnimationTimeout) {
      clearTimeout(this._openAnimationTimeout);
    }
    
    // Set the flag to prevent closing during the open animation sequence
    this.isOpening = true;
    
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // First add active class to make drawer visible
    this.classList.add('active');
    
    // Then trigger animation with a slight delay
    this._openAnimationTimeout = setTimeout(() => {
      this.classList.add('animate');
      document.body.classList.add('overflow-hidden');
      
      // Set a timeout to clear the isOpening flag
      setTimeout(() => {
        this.isOpening = false;
      }, 500); // Match to CSS transition time
    }, 10);

    // Remove any existing transitionend event listeners to prevent duplicates
    this.removeEventListener('transitionend', this._trapFocusOnOpen);
    
    // Define a reusable function for trap focus
    this._trapFocusOnOpen = () => {
      try {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        
        if (!containerToTrapFocusOn) return;
        
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        
        // Only call trapFocus if it exists as a function
        if (containerToTrapFocusOn && focusElement && typeof window.trapFocus === 'function') {
          window.trapFocus(containerToTrapFocusOn, focusElement);
        }
      } catch (e) {
        console.error('Error trapping focus in cart drawer:', e);
      }
    };

    // Set the event once
    this.addEventListener('transitionend', this._trapFocusOnOpen, { once: true });
  }

  close() {
    // Don't close if we're in the middle of rendering content or opening
    if (this.isRenderingContent || this.isOpening) {
      console.log('Prevented drawer close during content rendering or opening');
      return;
    }
    
    // Only proceed with close if drawer is active
    if (!this.classList.contains('active')) return;
    
    // Clean up any timeouts
    if (this._openAnimationTimeout) {
      clearTimeout(this._openAnimationTimeout);
    }
    
    if (this._renderTimeout) {
      clearTimeout(this._renderTimeout);
    }
    
    // First remove animation class to trigger slide out
    this.classList.remove('animate');
    
    // Then after animation completes, remove active class
    const transitionDuration = 300; // Match this to your CSS transition duration
    setTimeout(() => {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    }, transitionDuration);
    
    try {
      // Only call removeTrapFocus if it exists as a function
      if (typeof window.removeTrapFocus === 'function' && this.activeElement) {
        window.removeTrapFocus(this.activeElement);
      }
    } catch (e) {
      console.error('Error removing trap focus:', e);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    try {
      // Clear any existing render timeouts
      if (this._renderTimeout) {
        clearTimeout(this._renderTimeout);
      }
      
      // Set flag to prevent closing during render
      this.isRenderingContent = true;
      
      // First, check if drawer is already open. If not, open it
      if (!this.classList.contains('active')) {
        this.open();
      }
      
      // Maintain active and animated states during content updates
      if (!this.classList.contains('active')) {
        this.classList.add('active');
      }
      if (!this.classList.contains('animate')) {
        this.classList.add('animate');
      }
      
      // Wait for drawer to be fully open before rendering contents
      this._renderTimeout = setTimeout(() => {
        // Check if cart is empty and handle appropriately - don't remove is-empty class
        const isCartEmpty = parsedState.item_count === 0;
        const drawerInner = this.querySelector('.drawer__inner');
        
        if (drawerInner) {
          // Always maintain background - only toggle empty state class for structure
          if (isCartEmpty && !this.classList.contains('is-empty')) {
            this.classList.add('is-empty');
          } else if (!isCartEmpty && this.classList.contains('is-empty')) {
            this.classList.remove('is-empty');
          }
        }
        
        this.productId = parsedState.id;
        
        if (parsedState && parsedState.sections) {
          this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);

            if (!sectionElement || !parsedState.sections[section.id]) return;
            
            try {
              // Replace the inner content without affecting the main drawer
              const newContent = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
              if (newContent) {
                sectionElement.innerHTML = newContent;
              }
            } catch (error) {
              console.error(`Error updating section ${section.id}:`, error);
            }
          });
        }

        // Ensure overlay click handler is attached
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          // Use a private method to bind the event handler to maintain reference
          this._handleOverlayClick = this.close.bind(this);
          overlay.removeEventListener('click', this._handleOverlayClick);
          overlay.addEventListener('click', this._handleOverlayClick);
        }
        
        // Reset rendering flag after a longer delay to ensure animations complete
        setTimeout(() => {
          this.isRenderingContent = false;
        }, 300);
      }, 10); // Reduced delay for faster content update
    } catch (error) {
      console.error('Error rendering cart drawer contents:', error);
      this.isRenderingContent = false;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      if (!html) return '';
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const section = parsedHTML.querySelector(selector);
      return section ? section.innerHTML : '';
    } catch (error) {
      console.error('Error in getSectionInnerHTML:', error);
      return '';
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    try {
      if (!html) return null;
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      return parsedHTML.querySelector(selector);
    } catch (error) {
      console.error('Error in getSectionDOM:', error);
      return null;
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
